import React from 'react';
import { Helmet } from 'react-helmet';
import { useTheme } from "./ThemeContext";
import { Link } from 'react-router-dom';
import { motion } from "framer-motion";
import {
  FaSort, FaFileCode, FaKey, FaExchangeAlt, FaTextHeight, FaParagraph, FaAlignLeft,
  FaClipboardList, FaEraser, FaRandom, FaCode, FaCompress, FaCheckCircle,
  FaRegFileCode, FaShieldAlt, FaTerminal
} from 'react-icons/fa';
import AdComponent from './AdComponent';

export default function Home() {
  const { darkMode } = useTheme();

  const seoTitle = "All-in-One Online Tools - Text, Code, Image & More";
  const seoDescription = "Discover a collection of powerful online tools for text processing, code formatting, image conversion, encryption, and more. Free and easy to use.";
  const seoKeywords = "online tools, text tools, code formatter, JSON validator, regex tester, Base64 encoder, hash generator, JWT decoder, unit converter";

  const tools = [
    { id: 'case-converter', name: 'Text Case Converter', icon: <FaTextHeight className="text-blue-600" /> },
    { id: 'char-counter', name: 'Character & Word Counter', icon: <FaParagraph className="text-green-600" /> },
    { id: 'text-diff', name: 'Text Diff Checker', icon: <FaExchangeAlt className="text-purple-600" /> },
    { id: 'text-cleaner', name: 'Text Cleaner', icon: <FaEraser className="text-red-600" /> },
    { id: 'lorem-ipsum', name: 'Lorem Ipsum Generator', icon: <FaClipboardList className="text-yellow-600" /> },
    { id: 'markdown', name: 'Markdown to HTML Converter', icon: <FaFileCode className="text-indigo-600" /> },
    { id: 'encryption', name: 'Text Encryption & Decryption', icon: <FaKey className="text-teal-600" /> },
    { id: 'sorting', name: 'Text Sorter', icon: <FaSort className="text-pink-600" /> },
    { id: 'find-replace', name: 'Find & Replace', icon: <FaAlignLeft className="text-orange-600" /> },
    { id: 'beautifier', name: 'Text Beautifier', icon: <FaRandom className="text-gray-600" /> }
  ];

  const toolsCode = [
    { name: "Code Formatter", desc: "Beautify and format your code properly.", icon: <FaCode className="text-blue-500" size={32} />, link: "/codetools" },
    { name: "Code Minifier", desc: "Minify JavaScript, CSS, and HTML files.", icon: <FaCompress className="text-green-500" size={32} />, link: "/codetools" },
    { name: "JSON Validator", desc: "Validate and format JSON data.", icon: <FaCheckCircle className="text-yellow-500" size={32} />, link: "/jsonvalidator" },
    { name: "Regex Tester", desc: "Test and debug regular expressions.", icon: <FaTerminal className="text-indigo-500" size={32} />, link: "/regextester" },
    { name: "Base64 Encoder", desc: "Encode and decode Base64 strings.", icon: <FaKey className="text-purple-500" size={32} />, link: "/base64" },
    { name: "JWT Decoder", desc: "Decode and verify JWT tokens.", icon: <FaShieldAlt className="text-red-500" size={32} />, link: "/jwtdecoder" },
    { name: "HTML to Markdown", desc: "Convert HTML to Markdown format.", icon: <FaExchangeAlt className="text-pink-500" size={32} />, link: "/texttools" },
    { name: "Hash Generator", desc: "Generate MD5, SHA-256, and other hashes.", icon: <FaRegFileCode className="text-gray-500" size={32} />, link: "/hashgenerator" },
  ];

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={seoKeywords} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://webutility.online/" />
        <meta property="og:image" content="https://webutility.online/og-image.jpg" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Online Utility Tools",
            "url": "https://webutility.online/",
            "description": seoDescription,
            "keywords": seoKeywords.split(", ")
          })}
        </script>
      </Helmet>

      <main className="container mx-auto px-4 pb-12 ">

        
        <header className={`py-16 px-4 text-center rounded-b-3xl shadow ${darkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-800'} mb-3`}>
          <motion.div
            className="max-w-5xl mx-auto"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.h1
              className="text-4xl md:text-5xl font-bold leading-tight mb-4"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              All Your Favorite Online Tools – 100% Free
            </motion.h1>

            <motion.p
              className="text-lg md:text-xl mb-6 text-gray-600 dark:text-gray-300"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.6 }}
            >
              WebUtility.online provides fast, free tools for developers, writers, designers, and everyday users — no sign-up, no limits. Forever.
            </motion.p>

            <motion.div
              className="flex flex-wrap justify-center gap-4 mt-6"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.7, duration: 0.5 }}
            >
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/texttools" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl shadow transition">
                  Explore Text Tools
                </Link>
              </motion.div>

              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/codetools" className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl shadow transition">
                  Try Code Tools
                </Link>
              </motion.div>

              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/imagetools" className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-xl shadow transition">
                  Image Tools
                </Link>
              </motion.div>
            </motion.div>
          </motion.div>
        </header>
        {/* MAIN CONTENT */}
        <section className={`p-8 rounded-xl shadow-lg ${darkMode ? 'bg-gray-800 text-white' : 'bg-white'} mb-6`}>
          <h1 className="text-4xl font-bold mb-4">All-in-One Web Utilities</h1>
          <p className="text-lg text-gray-600 dark:text-gray-300">Access text, image, code, and conversion tools—all in one place.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 mt-8">
            {[
              { title: "Image Tools", icon: "📷", description: "Convert, compress, and resize images.", route: "./imagetools" },
              { title: "Text Tools", icon: "📝", description: "Transform and process text.", route: "./texttools" },
              { title: "Color Tools", icon: "🎨", description: "Work with color formats.", route: "./colortools" },
              { title: "Code Tools", icon: "💻", description: "Format and debug code.", route: "./codetools" },
              { title: "Unit Converter", icon: "📏", description: "Convert measurement units.", route: "./unitconverter" }
            ].map((tool, idx) => (
              <Link to={tool.route} key={idx} className="p-5 rounded-xl bg-gray-50 hover:bg-gray-100 dark:bg-gray-900 dark:hover:bg-gray-700 transition transform hover:scale-105 shadow">
                <div className="text-3xl mb-2">{tool.icon}</div>
                <h3 className="text-lg font-semibold">{tool.title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{tool.description}</p>
              </Link>
            ))}
          </div>
        </section>

        {/* TEXT TOOLS */}
        <section className={`p-8 rounded-xl shadow-lg ${darkMode ? 'bg-gray-800 text-white' : 'bg-white'} mb-6`}>
          <h2 className="text-3xl font-bold mb-4">Text Tools</h2>
          <p className="mb-4 text-gray-600 dark:text-gray-300">Format, count, diff, and beautify your text effortlessly.</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {tools.map(tool => (
              <Link key={tool.id} to="/texttools" className="flex flex-col items-center p-4 bg-gray-50 hover:bg-gray-100 dark:bg-gray-900 dark:hover:bg-gray-700 rounded-lg shadow transition hover:scale-105">
                <div className="text-2xl mb-1">{tool.icon}</div>
                <p className="text-sm font-medium text-center">{tool.name}</p>
              </Link>
            ))}
          </div>
        </section>

        {/* CODE TOOLS */}
        <section className={`p-8 rounded-xl shadow-lg ${darkMode ? 'bg-gray-800 text-white' : 'bg-white'}`}>
          <h2 className="text-3xl font-bold mb-4">Code Tools</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">Tools to help developers test, encode, and optimize code.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
            {toolsCode.map((tool, index) => (
              <Link key={index} to={tool.link} className="p-4 bg-white dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg shadow-md flex flex-col items-center text-center transition-transform hover:scale-105">
                {tool.icon}
                <h4 className="mt-2 text-lg font-semibold">{tool.name}</h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">{tool.desc}</p>
              </Link>
            ))}
          </div>
        </section>
        {/* RESOURCE GUIDES */}
        <section className={`p-8 rounded-xl shadow-lg mt-12 ${darkMode ? 'bg-gray-800 text-white' : 'bg-white'}`}>
          <h2 className="text-3xl font-bold mb-4">Helpful Guides & How-Tos</h2>
          <p className="mb-6 text-gray-600 dark:text-gray-300">Explore our expert-written guides on how to use the tools effectively and get the most out of WebUtility.online.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "How to Beautify Your Code Online",
                link: "/guides/beautify-code-online",
                desc: "Learn how to format messy HTML, CSS, and JavaScript code for readability in just a few clicks."
              },
              {
                title: "Guide to Encoding and Decoding with Base64",
                link: "/guides/base64-encoding-guide",
                desc: "Understand what Base64 is and how you can easily encode or decode text and files using our tool."
              },
              {
                title: "Top 5 Ways to Clean Text Before Analysis",
                link: "/guides/clean-text-tips",
                desc: "Explore tips for removing unnecessary spaces, special characters, or formatting from raw text."
              },
              {
                title: "How to Count Words and Characters Efficiently",
                link: "/guides/word-char-counter",
                desc: "Find out how word and character count tools can improve your content writing and SEO optimization."
              },
              {
                title: "Using Regex to Search & Replace Like a Pro",
                link: "/guides/regex-search-replace",
                desc: "A beginner-friendly introduction to regular expressions and our live tester tool."
              },
              {
                title: "Convert HTML to Markdown: Why and How",
                link: "/guides/html-to-markdown",
                desc: "Learn when to convert HTML to Markdown and how to do it with our instant tool."
              }
            ].map((guide, i) => (
              <Link to={guide.link} key={i} className="block p-4 rounded-lg bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-700 shadow transition hover:scale-105">
                <h3 className="text-lg font-semibold mb-1">{guide.title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">{guide.desc}</p>
              </Link>
            ))}
          </div>
        </section>



      </main>
    </>
  );
}
